import { Component } from '@theme/component';

const desktopMediaQuery = window.matchMedia('(min-width: 990px)');

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /** @type {ResizeObserver | null} */
  #stickyResizeObserver = null;

  /**
   * Whether the component is currently using the desktop static-row layout.
   */
  get isDesktopStatic() {
    return this.hasAttribute('desktop-static') && desktopMediaQuery.matches;
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    desktopMediaQuery.addEventListener('change', this.#handleBreakpointChange);

    if (this.hasAttribute('sticky-with-header')) {
      const announcement = this.closest('.announcement-bar');
      if (announcement) {
        this.#stickyResizeObserver = new ResizeObserver(this.#updateStickyHeight);
        this.#stickyResizeObserver.observe(announcement);
        this.#setStickyHeight(announcement.getBoundingClientRect().height);
      }
    }

    this.#syncResponsiveMode();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.suspend();
    this.#stickyResizeObserver?.disconnect();
    this.#stickyResizeObserver = null;
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    desktopMediaQuery.removeEventListener('change', this.#handleBreakpointChange);
    if (this.hasAttribute('sticky-with-header')) this.#setStickyHeight(0);
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay || this.isDesktopStatic) return;

    this.paused = false;
    this.suspend();

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /**
   * Enables the slideshow below the theme's 990px desktop breakpoint and
   * exposes every announcement when the merchant selected a static desktop row.
   */
  #syncResponsiveMode() {
    if (this.isDesktopStatic) {
      this.suspend();
      this.removeAttribute('aria-live');
      this.refs.slides?.forEach((slide) => slide.setAttribute('aria-hidden', 'false'));
      return;
    }

    if (this.autoplay) this.setAttribute('aria-live', 'polite');
    this.current = this.#current;
    this.play();
  }

  #handleBreakpointChange = () => this.#syncResponsiveMode();

  /** @param {ResizeObserverEntry[]} entries */
  #updateStickyHeight = ([entry]) => {
    const height = entry?.borderBoxSize[0]?.blockSize ?? entry?.contentRect.height ?? 0;
    this.#setStickyHeight(height);
  };

  /** @param {number} height */
  #setStickyHeight(height) {
    document.body.style.setProperty('--sticky-announcement-height', `${Math.round(height)}px`);
    document.dispatchEvent(new CustomEvent('stickyAnnouncementResize'));
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
